import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { testimonials, aggregateRating, type Testimonial } from "@/data/testimonials";

/** Pick the best text for the current language */
function getLocalizedText(review: Testimonial, lang: string): string {
  if (lang === "fr") return review.textFr ?? review.textEn ?? review.text;
  return review.textEn ?? review.text;
}

/** "Patrick Morvan" → "Patrick M." | "Jaydone" → "Jaydone" */
function formatName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  return `${parts[0]} ${last[0].toUpperCase()}.`;
}

const StarRating = ({ value }: { value: number }) => (
  <div className="flex gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        className={`h-4 w-4 ${s <= value ? "fill-pagoda-gold text-pagoda-gold" : "text-muted-foreground/30"}`}
      />
    ))}
  </div>
);

const VISIBLE = 3;        // cards shown at once on desktop
const INTERVAL_MS = 5000; // auto-advance delay

const TestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const total = testimonials.length;

  const advance = useCallback((dir: 1 | -1) => {
    setDirection(dir);
    setCurrent((c) => (c + dir + total) % total);
  }, [total]);

  // Auto-advance
  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => advance(1), INTERVAL_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [current, paused, advance]);

  // Visible indices (wrapping)
  const visibleIndices = Array.from({ length: VISIBLE }, (_, i) => (current + i) % total);

  return (
    <section
      id="testimonials"
      className="py-20 lg:py-28 bg-secondary overflow-hidden"
      aria-label="Customer reviews"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container mx-auto px-4">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-accent mb-3 block">
            {t("testimonials.badge", "Trusted by members worldwide")}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            {t("testimonials.title", "What Our Members Say")}
          </h2>

          {/* Aggregate rating badge */}
          <div className="inline-flex items-center gap-3 bg-card border border-border rounded-full px-6 py-3 shadow-sm">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map((s) => (
                <Star key={s} className="h-4 w-4 fill-pagoda-gold text-pagoda-gold" />
              ))}
            </div>
            <span className="font-display font-bold text-foreground">
              {aggregateRating.ratingValue}
            </span>
            <span className="text-muted-foreground font-body text-sm">
              {aggregateRating.reviewCount} {t("testimonials.reviews", "Google reviews")}
            </span>
          </div>
        </motion.div>

        {/* Carousel */}
        <div className="relative">

          {/* Prev */}
          <button
            onClick={() => advance(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-card border border-border shadow-sm flex items-center justify-center hover:bg-accent/10 transition-colors"
            aria-label="Previous review"
          >
            <ChevronLeft className="h-5 w-5 text-foreground" />
          </button>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mx-6">
            <AnimatePresence mode="popLayout" initial={false}>
              {visibleIndices.map((idx) => {
                const review = testimonials[idx];
                return (
                  <motion.article
                    key={`${idx}-${review.author}`}
                    initial={{ opacity: 0, x: direction * 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: direction * -60 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="bg-card rounded-xl p-6 shadow-card flex flex-col"
                    itemScope
                    itemType="https://schema.org/Review"
                  >
                    <meta itemProp="datePublished" content={review.datePublished} />
                    <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                      <meta itemProp="ratingValue" content={String(review.ratingValue)} />
                      <meta itemProp="bestRating" content="5" />
                    </div>

                    <Quote className="h-5 w-5 text-accent/30 mb-3 flex-shrink-0" />

                    <p
                      className="text-sm font-body text-muted-foreground leading-relaxed flex-1 mb-4 line-clamp-5"
                      itemProp="reviewBody"
                    >
                      {getLocalizedText(review, lang)}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div>
                        <span
                          className="font-display font-semibold text-foreground text-sm block"
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          {/* Full name in schema, anonymized display */}
                          <meta itemProp="name" content={review.author} />
                          {formatName(review.author)}
                        </span>
                        {review.country && (
                          <span className="text-xs text-muted-foreground font-body">
                            {review.country}
                          </span>
                        )}
                      </div>
                      <StarRating value={review.ratingValue} />
                    </div>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Next */}
          <button
            onClick={() => advance(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-card border border-border shadow-sm flex items-center justify-center hover:bg-accent/10 transition-colors"
            aria-label="Next review"
          >
            <ChevronRight className="h-5 w-5 text-foreground" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                visibleIndices.includes(i)
                  ? "w-6 bg-accent"
                  : "w-1.5 bg-border hover:bg-muted-foreground/50"
              }`}
              aria-label={`Go to review ${i + 1}`}
            />
          ))}
        </div>

        {/* Source note */}
        <p className="text-center text-xs text-muted-foreground/50 font-body mt-6">
          {t("testimonials.sourceNote", "Reviews from Google Business Profile · Names anonymized for privacy")}
        </p>

      </div>
    </section>
  );
};

export default TestimonialsSection;
